/**
 * アプリケーション設定ストア
 *
 * Zustandを使用してアプリケーション設定を管理します。
 * localStorageと連携して永続化を行います。
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  AppSettings,
  GraphSettings,
  DEFAULT_GRAPH_SETTINGS,
  loadSettings,
  addTrustedUrl as addTrustedUrlToStorage,
  removeTrustedUrl as removeTrustedUrlFromStorage,
  resetSettings as resetSettingsInStorage,
  updateGraphSettings as updateGraphSettingsInStorage,
} from '../services/settingsService';

interface SettingsStore {
  // 状態
  settings: AppSettings;
  isLoaded: boolean;

  // アクション
  loadFromStorage: () => void;
  addTrustedUrl: (url: string) => void;
  removeTrustedUrl: (url: string) => void;
  updateGraphSettings: (graphSettings: Partial<GraphSettings>) => void;
  resetSettings: () => void;

  // ゲッター
  getTrustedUrls: () => string[];
  getGraphSettings: () => GraphSettings;
  getMaxFacetCategories: () => number;
}

export const useSettingsStore = create<SettingsStore>()(
  devtools(
    (set, get) => ({
      // 初期状態
      settings: {
        version: 2,
        security: {
          trustedUrls: [],
        },
        graph: { ...DEFAULT_GRAPH_SETTINGS },
      },
      isLoaded: false,

      // ストレージから設定を読み込む
      loadFromStorage: () => {
        const settings = loadSettings();
        set({ settings, isLoaded: true });
      },

      // 信頼URLを追加
      addTrustedUrl: (url: string) => {
        const newSettings = addTrustedUrlToStorage(url);
        set({ settings: newSettings });
      },

      // 信頼URLを削除
      removeTrustedUrl: (url: string) => {
        const newSettings = removeTrustedUrlFromStorage(url);
        set({ settings: newSettings });
      },

      // グラフ設定を更新
      updateGraphSettings: (graphSettings: Partial<GraphSettings>) => {
        const newSettings = updateGraphSettingsInStorage(graphSettings);
        set({ settings: newSettings });
      },

      // 設定をリセット
      resetSettings: () => {
        const newSettings = resetSettingsInStorage();
        set({ settings: newSettings });
      },

      // 信頼URLリストを取得
      getTrustedUrls: () => {
        return get().settings.security.trustedUrls;
      },

      // グラフ設定を取得
      getGraphSettings: () => {
        return get().settings.graph;
      },

      // Facetの最大カテゴリ数を取得
      getMaxFacetCategories: () => {
        return get().settings.graph.maxFacetCategories;
      },
    }),
    { name: 'SettingsStore' }
  )
);

// アプリケーション起動時に設定を読み込む
// このファイルがインポートされた時点で自動的に読み込む
if (typeof window !== 'undefined') {
  useSettingsStore.getState().loadFromStorage();
}
